(() => {
  const SELECTORS = {
    article: '[data-journal-article]',
    content: '.journal-article__content',
    progress: '[data-journal-progress]',
    progressBar: '[data-journal-progress-bar]',
    toc: '[data-journal-toc]',
    tocList: '[data-journal-toc-list]',
    copyButtons: '[data-journal-copy-link]',
    copyMessages: '[data-journal-copy-message]'
  };

  const slugify = (value) =>
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  const createUniqueHeadingId = (heading, index, usedIds) => {
    const fallback = `story-section-${index + 1}`;
    const baseId = heading.id || slugify(heading.textContent) || fallback;
    let headingId = baseId;
    let suffix = 2;

    while (
      usedIds.has(headingId) ||
      (document.getElementById(headingId) &&
        document.getElementById(headingId) !== heading)
    ) {
      headingId = `${baseId}-${suffix}`;
      suffix += 1;
    }

    usedIds.add(headingId);
    return headingId;
  };

  const initialiseTableOfContents = (article) => {
    const content = article.querySelector(SELECTORS.content);
    const toc = article.querySelector(SELECTORS.toc);
    const tocList = article.querySelector(SELECTORS.tocList);

    if (!content || !toc || !tocList) return;

    const headings = Array.from(content.querySelectorAll('h2'));
    tocList.innerHTML = '';

    if (headings.length < 2) {
      toc.hidden = true;
      return;
    }

    const usedIds = new Set();

    headings.forEach((heading, index) => {
      const headingId = createUniqueHeadingId(heading, index, usedIds);
      heading.id = headingId;
      heading.classList.add('journal-scroll-target');

      const item = document.createElement('li');
      item.className = 'journal-toc__item';

      const link = document.createElement('a');
      link.className = 'journal-toc__link';
      link.href = `#${headingId}`;
      link.textContent = heading.textContent.trim();
      link.dataset.journalTocLink = headingId;

      link.addEventListener('click', (event) => {
        event.preventDefault();

        heading.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
          block: 'start'
        });

        window.history.replaceState(null, '', `#${headingId}`);
      });

      item.appendChild(link);
      tocList.appendChild(item);
    });

    toc.hidden = false;

    if ('IntersectionObserver' in window) {
      const links = Array.from(tocList.querySelectorAll('.journal-toc__link'));

      const setActiveLink = (headingId) => {
        links.forEach((link) => {
          const isActive = link.dataset.journalTocLink === headingId;
          link.classList.toggle('is-active', isActive);

          if (isActive) {
            link.setAttribute('aria-current', 'location');
          } else {
            link.removeAttribute('aria-current');
          }
        });
      };

      const observer = new IntersectionObserver(
        (entries) => {
          const visibleEntry = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

          if (visibleEntry) setActiveLink(visibleEntry.target.id);
        },
        {
          rootMargin: '-18% 0px -68% 0px',
          threshold: 0
        }
      );

      headings.forEach((heading) => observer.observe(heading));

      if (headings[0]) {
        setActiveLink(headings[0].id);
      }
    }
  };

  const initialiseReadingProgress = (article) => {
    const progress = document.querySelector(SELECTORS.progress);
    const progressBar = document.querySelector(SELECTORS.progressBar);

    if (!progress || !progressBar) return;

    let ticking = false;

    const updateProgress = () => {
      const articleTop = article.getBoundingClientRect().top + window.scrollY;
      const articleBottom = articleTop + article.offsetHeight;
      const viewportReference = window.scrollY + window.innerHeight * 0.2;
      const readableDistance = Math.max(
        articleBottom - articleTop - window.innerHeight * 0.8,
        1
      );

      const percentage = Math.min(
        100,
        Math.max(0, ((viewportReference - articleTop) / readableDistance) * 100)
      );

      progressBar.style.transform = `scaleX(${percentage / 100})`;
      progress.setAttribute('aria-valuenow', String(Math.round(percentage)));
      ticking = false;
    };

    const requestUpdate = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateProgress);
        ticking = true;
      }
    };

    if (window.__journalProgressHandler) {
      window.removeEventListener('scroll', window.__journalProgressHandler);
      window.removeEventListener('resize', window.__journalProgressHandler);
    }

    window.__journalProgressHandler = requestUpdate;

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    updateProgress();
  };

  const initialiseCopyLinks = (article) => {
    const buttons = Array.from(article.querySelectorAll(SELECTORS.copyButtons));
    const messages = Array.from(article.querySelectorAll(SELECTORS.copyMessages));

    if (!buttons.length) return;

    const showMessage = (text) => {
      messages.forEach((message) => {
        message.textContent = text;
      });

      window.setTimeout(() => {
        messages.forEach((message) => {
          message.textContent = '';
        });
      }, 2500);
    };

    buttons.forEach((button) => {
      if (button.dataset.copyInitialised === 'true') return;

      button.addEventListener('click', async () => {
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(window.location.href);
          } else {
            const temporaryInput = document.createElement('textarea');
            temporaryInput.value = window.location.href;
            temporaryInput.setAttribute('readonly', '');
            temporaryInput.style.position = 'fixed';
            temporaryInput.style.opacity = '0';
            temporaryInput.style.pointerEvents = 'none';

            document.body.appendChild(temporaryInput);
            temporaryInput.select();
            temporaryInput.setSelectionRange(0, temporaryInput.value.length);

            const copied = document.execCommand('copy');
            temporaryInput.remove();

            if (!copied) {
              throw new Error('Copy command failed.');
            }
          }

          showMessage('Story link copied.');
        } catch (error) {
          showMessage('Press and hold the address bar to copy the Story link.');
        }
      });

      button.dataset.copyInitialised = 'true';
    });
  };

  const enhanceArticleMedia = (article) => {
    const content = article.querySelector(SELECTORS.content);
    if (!content) return;

    content.querySelectorAll('iframe').forEach((iframe) => {
      if (iframe.parentElement?.classList.contains('journal-video')) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'journal-video';

      iframe.parentNode.insertBefore(wrapper, iframe);
      wrapper.appendChild(iframe);
    });
  };

  const initialiseTocToggle = (article) => {
    const toc = article.querySelector(SELECTORS.toc);
    const toggle = article.querySelector('[data-journal-toc-toggle]');

    if (!toc || !toggle || toggle.dataset.initialised === 'true') return;

    toggle.addEventListener('click', () => {
      const collapsed = toc.classList.toggle('is-collapsed');
      toggle.setAttribute('aria-expanded', String(!collapsed));
    });

    toggle.dataset.initialised = 'true';
  };

  const initialiseReadingComplete = (article) => {
    const completion = article.querySelector('[data-journal-reading-complete]');
    const content = article.querySelector(SELECTORS.content);

    if (!completion || !content) return;

    const activate = () => completion.classList.add('is-visible');

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              activate();
              observer.disconnect();
            }
          });
        },
        {
          rootMargin: '0px 0px -12% 0px',
          threshold: 0.2
        }
      );

      observer.observe(completion);
    } else {
      activate();
    }
  };

  const initialiseFootnotes = (article) => {
    const content = article.querySelector(SELECTORS.content);
    if (!content) return;

    const references = Array.from(
      content.querySelectorAll('a[href^="#footnote-"], a[data-footnote-ref]')
    );

    references.forEach((reference, index) => {
      const href = reference.getAttribute('href');
      const targetId = href && href.startsWith('#')
        ? href.slice(1)
        : reference.dataset.footnoteRef;

      if (!targetId) return;

      const footnote = content.querySelector(`#${CSS.escape(targetId)}`);
      if (!footnote) return;

      if (!reference.id) {
        reference.id = `footnote-reference-${index + 1}`;
      }

      reference.classList.add('journal-footnote-reference');
      footnote.classList.add('journal-footnote');

      if (!footnote.querySelector('.journal-footnote__backlink')) {
        const backlink = document.createElement('a');
        backlink.className = 'journal-footnote__backlink';
        backlink.href = `#${reference.id}`;
        backlink.setAttribute('aria-label', 'Return to footnote reference');
        backlink.textContent = '↩';
        footnote.appendChild(document.createTextNode(' '));
        footnote.appendChild(backlink);
      }
    });
  };

  const initialiseRevealAnimations = (article) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const revealItems = Array.from(
      article.querySelectorAll(
        [
          '.journal-article__header',
          '.journal-article__hero',
          '.journal-toc',
          '.journal-article__content > :not(.dd-editorial-story)',
          '.journal-article__content > .dd-editorial-story > *',
          '.journal-reading-complete',
          '.journal-story-navigation',
          '.journal-related',
          '.journal-newsletter',
          '.journal-article__footer'
        ].join(',')
      )
    );

    revealItems.forEach((item) => item.classList.add('journal-reveal'));

    if (!('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.08
      }
    );

    revealItems.forEach((item) => observer.observe(item));
  };

  const initialiseAccessibleCarousels = () => {
    const carouselSelectors = [
      '.journal-latest-stories .journal-story-grid',
      '.journal-topic-navigation .journal-topic-navigation__grid',
      '.journal-featured-collections__grid',
      '.journal-featured-collection__grid',
      '.journal-collections-grid',
      '.journal-collection-grid'
    ];

    const carousels = Array.from(
      document.querySelectorAll(carouselSelectors.join(','))
    );

    carousels.forEach((carousel, carouselIndex) => {
      if (carousel.dataset.carouselInitialised === 'true') return;

      carousel.setAttribute('role', 'region');
      carousel.setAttribute('aria-roledescription', 'carousel');

      if (!carousel.hasAttribute('aria-label')) {
        carousel.setAttribute(
          'aria-label',
          `Journal carousel ${carouselIndex + 1}`
        );
      }

      carousel.tabIndex = 0;

      const items = Array.from(carousel.children);

      items.forEach((item, itemIndex) => {
        item.setAttribute('role', 'group');
        item.setAttribute('aria-roledescription', 'slide');
        item.setAttribute(
          'aria-label',
          `${itemIndex + 1} of ${items.length}`
        );
      });

      carousel.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          return;
        }

        event.preventDefault();

        const direction =
          document.documentElement.dir === 'rtl' ? -1 : 1;

        if (event.key === 'Home') {
          carousel.scrollTo({ left: 0, behavior: 'smooth' });
          return;
        }

        if (event.key === 'End') {
          carousel.scrollTo({
            left: carousel.scrollWidth,
            behavior: 'smooth'
          });
          return;
        }

        const distance = Math.max(carousel.clientWidth * 0.82, 260);
        const amount =
          event.key === 'ArrowRight'
            ? distance * direction
            : -distance * direction;

        carousel.scrollBy({ left: amount, behavior: 'smooth' });
      });

      carousel.dataset.carouselInitialised = 'true';
    });
  };

  const initialiseExternalLinks = (article) => {
    const currentHost = window.location.host;

    article.querySelectorAll('.journal-article__content a[href]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.href);

        if (
          url.host &&
          url.host !== currentHost &&
          !link.hasAttribute('target')
        ) {
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
        }
      } catch (error) {
        // Ignore malformed or non-HTTP links.
      }
    });
  };

  const initialiseFocusManagement = (article) => {
    const skipLink = article.querySelector('.journal-article__skip-link');
    const content = article.querySelector(SELECTORS.content);

    if (!skipLink || !content || skipLink.dataset.initialised === 'true') {
      return;
    }

    skipLink.addEventListener('click', () => {
      window.setTimeout(() => content.focus({ preventScroll: true }), 0);
    });

    skipLink.dataset.initialised = 'true';
  };

  const initialiseJournal = () => {
    initialiseAccessibleCarousels();

    const article = document.querySelector(SELECTORS.article);
    if (!article) return;

    initialiseTableOfContents(article);
    initialiseTocToggle(article);
    initialiseReadingProgress(article);
    initialiseCopyLinks(article);
    enhanceArticleMedia(article);
    initialiseReadingComplete(article);
    initialiseFootnotes(article);
    initialiseRevealAnimations(article);
    initialiseAccessibleCarousels();
    initialiseExternalLinks(article);
    initialiseFocusManagement(article);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseJournal, { once: true });
  } else {
    initialiseJournal();
  }

  document.addEventListener('shopify:section:load', initialiseJournal);
})();
/* ==========================================================================
   JOURNAL HOMEPAGE — PACKAGES 2, 3 & 4
   ========================================================================== */
(() => {
  const HOME_ROOTS = [
    '.section-journal-header',
    '.section-journal-featured-story',
    '.section-journal-latest-stories',
    '.section-journal-topic-navigation',
    '.section-journal-editors-pick',
    '.section-journal-featured-collections',
    '.section-journal-vintage-inspiration',
    '.section-journal-newsletter',
    '.section-journal-footer'
  ];

  const CAROUSELS = [
    '.section-journal-header .journal-categories',
    '.section-journal-latest-stories .journal-story-grid',
    '.section-journal-topic-navigation .journal-topic-navigation__grid',
    '.section-journal-featured-collections .journal-featured-collections__grid'
  ];

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initialiseHomepageImages = (scope = document) => {
    scope.querySelectorAll(
      HOME_ROOTS.map((root) => `${root} img`).join(',')
    ).forEach((image) => {
      image.decoding = 'async';

      if (
        !image.closest('.section-journal-featured-story') &&
        !image.hasAttribute('loading')
      ) {
        image.loading = 'lazy';
      }
    });
  };

  const initialiseHomepageCarousels = (scope = document) => {
    scope.querySelectorAll(CAROUSELS.join(',')).forEach((carousel, index) => {
      if (carousel.dataset.homeCarouselInitialised === 'true') return;

      carousel.setAttribute('role', 'region');
      carousel.setAttribute('aria-roledescription', 'carousel');

      if (!carousel.hasAttribute('aria-label')) {
        const section = carousel.closest('section');
        const heading = section?.querySelector('h1, h2');
        carousel.setAttribute(
          'aria-label',
          heading?.textContent.trim() || `Journal carousel ${index + 1}`
        );
      }

      carousel.tabIndex = 0;

      const items = Array.from(carousel.children);
      items.forEach((item, itemIndex) => {
        item.setAttribute('role', 'group');
        item.setAttribute('aria-roledescription', 'slide');
        item.setAttribute('aria-label', `${itemIndex + 1} of ${items.length}`);
      });

      carousel.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          return;
        }

        event.preventDefault();
        const behaviour = prefersReducedMotion() ? 'auto' : 'smooth';

        if (event.key === 'Home') {
          carousel.scrollTo({ left: 0, behavior: behaviour });
          return;
        }

        if (event.key === 'End') {
          carousel.scrollTo({
            left: carousel.scrollWidth,
            behavior: behaviour
          });
          return;
        }

        const firstItem = items[0];
        const computedGap = parseFloat(
          window.getComputedStyle(carousel).columnGap ||
          window.getComputedStyle(carousel).gap ||
          '0'
        );
        const amount = (firstItem?.getBoundingClientRect().width || carousel.clientWidth * 0.82) + computedGap;
        const rtlMultiplier = document.documentElement.dir === 'rtl' ? -1 : 1;
        const direction = event.key === 'ArrowRight' ? 1 : -1;

        carousel.scrollBy({
          left: amount * direction * rtlMultiplier,
          behavior: behaviour
        });
      });

      carousel.dataset.homeCarouselInitialised = 'true';
    });
  };

  const initialiseHomepageReveals = (scope = document) => {
    const items = Array.from(
      scope.querySelectorAll(
        [
          '.section-journal-header .journal-header__inner',
          '.section-journal-header .journal-categories',
          '.section-journal-featured-story .journal-section-heading',
          '.section-journal-featured-story .journal-featured__card',
          '.section-journal-latest-stories .journal-section-heading',
          '.section-journal-latest-stories .journal-story-grid__item',
          '.section-journal-latest-stories .journal-latest-stories__footer',
          '.section-journal-topic-navigation .journal-section-heading',
          '.section-journal-topic-navigation .journal-topic-card',
          '.section-journal-editors-pick .journal-section-heading',
          '.section-journal-editors-pick article',
          '.section-journal-featured-collections .journal-section-heading',
          '.section-journal-featured-collections .journal-featured-collections__card',
          '.section-journal-vintage-inspiration .journal-section-heading',
          '.section-journal-vintage-inspiration .journal-vintage-inspiration__split',
          '.section-journal-newsletter .journal-section-heading',
          '.section-journal-newsletter .journal-newsletter__panel',
          '.section-journal-footer .journal-section-heading',
          '.section-journal-footer .journal-editorial-footer__content',
          '.section-journal-footer .journal-editorial-footer__bottom'
        ].join(',')
      )
    );

    if (!items.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    items.forEach((item, index) => {
      if (item.dataset.homeRevealInitialised === 'true') return;
      item.classList.add('journal-home-reveal');
      item.style.transitionDelay = `${Math.min(index % 4, 3) * 55}ms`;
      item.dataset.homeRevealInitialised = 'true';
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -7% 0px',
        threshold: 0.06
      }
    );

    items.forEach((item) => {
      if (!item.classList.contains('is-visible')) observer.observe(item);
    });
  };

  const initialiseNewsletterFeedback = (scope = document) => {
    scope.querySelectorAll('.section-journal-newsletter').forEach((section) => {
      const message = section.querySelector('.journal-newsletter__message');
      if (message && !message.hasAttribute('role')) {
        message.setAttribute('role', 'status');
      }
    });
  };

  const initialiseJournalHomepage = (scope = document) => {
    initialiseHomepageImages(scope);
    initialiseHomepageCarousels(scope);
    initialiseHomepageReveals(scope);
    initialiseNewsletterFeedback(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialiseJournalHomepage(document);
    }, { once: true });
  } else {
    initialiseJournalHomepage(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseJournalHomepage(event.target);
  });
})();